import { z } from 'zod';
// AI-search visibility — is this site legible to the engines that now answer
// buying questions? Sibling of mag-seo-audit, and it keeps that file's
// contract: the evidence is gathered DETERMINISTICALLY by the glue, the agent
// may only reason over what it is handed, and it must return 'all_clear'
// rather than invent busywork.
//
// It is a separate card from seo_audit on purpose. `decision` is one field per
// output and 'all_clear' gates card creation, so a site that is clean on
// classic SEO but invisible to ChatGPT has no way to be represented inside the
// seo_audit output at all.
/** Which lever a finding pulls.
 *
 *  - `retrievability`  can the engines' crawlers fetch the site (robots.txt).
 *  - `extractability`  can a non-JS crawler read what a browser sees.
 *  - `structured_data` schema.org / JSON-LD entity grounding.
 *  - `answer_shape`    is the copy quotable — the read only the agent can do.
 *  - `off_domain`      other people's pages: does a page that answers the
 *                      founder's buying question name them (spec 28d71507
 *                      slice 2). The only area here that is not about the
 *                      founder's own site, and the only one whose fix is an
 *                      outreach rather than an edit. */
export const aiVisibilityAreaSchema = z.enum([
    'retrievability',
    'extractability',
    'structured_data',
    'answer_shape',
    'off_domain',
]);
/** A paste-ready fix.
 *
 *  The site builder is parked (debd3a0e), so every brand Mag onboards now is a
 *  site Mag can read but not write. Advice a founder has to translate into
 *  markup is advice most founders will not act on, so where a fix IS literal
 *  text we hand over the literal text. */
export const aiVisibilitySnippetSchema = z.object({
    /** What the founder is pasting — a hint for syntax highlighting, nothing
     *  more.
     *
     *  `.catch` is load-bearing: the first live run returned a language outside
     *  the set and the WHOLE audit was thrown away over a highlighting hint.
     *  An unexpected value now degrades to 'html' instead of costing the card. */
    language: z.enum(['html', 'json-ld', 'robots.txt', 'text', 'xml']).catch('html'),
    /** Where it goes, in the founder's terms ("in the <head> of every page",
     *  "at the top of your robots.txt"). Framework-specific when we know the
     *  builder — Webflow and WordPress put custom head code in different places. */
    where: z.string().min(1).max(300),
    /** The literal text to paste. Not a template, not a description. */
    code: z.string().min(1).max(4000),
});
export const aiVisibilityFindingSchema = z.object({
    area: aiVisibilityAreaSchema,
    severity: z.enum(['high', 'medium', 'low']),
    /** A fact lifted from the evidence — the blocked agent and engine, the
     *  measured word counts, the literal copy. Never a number we didn't measure.
     *
     *  Generous cap. 600 was set by analogy with mag-seo-audit and the first live
     *  run blew straight through it: an observation that cites several crawler
     *  verdicts and two word counts is simply longer than one quoting a single
     *  Lighthouse audit. The UI truncates; the schema should not be the thing
     *  that decides a real finding is inadmissible. */
    observation: z.string().min(1).max(1200),
    /** The concrete fix. Short, imperative, lowercase (Mag voice). */
    recommendation: z.string().min(1).max(500),
    /** Present only when the fix is literal text the founder can paste. */
    snippet: aiVisibilitySnippetSchema.nullish(),
});
export const aiVisibilityOutputSchema = z.discriminatedUnion('decision', [
    z.object({
        decision: z.literal('audit'),
        /** Generous, and for the same measured reason `observation` was widened
         *  from 600 to 1200: a cap set by analogy throws away an entire run when a
         *  real answer runs one character long, and the UI truncates anyway. */
        summary: z.string().min(1).max(600),
        findings: z.array(aiVisibilityFindingSchema).min(1).max(8),
    }),
    z.object({
        decision: z.literal('all_clear'),
        /** Was 400, and a live run on 2026-09-30 blew it: the whole audit threw
         *  with LLMInvalidRequestError and the founder would have seen nothing.
         *
         *  The cause is structural rather than a one-off. all_clear was given the
         *  SHORTEST cap on the theory that "everything is fine" needs fewer words,
         *  but an all_clear has to account for every area it checked — and
         *  off_domain made that five, one of which now summarises pages fetched
         *  across several buying queries. The shortest cap sat on the branch whose
         *  content grew. */
        summary: z.string().min(1).max(600),
    }),
]);
// ─── the deterministic evidence ───────────────────────────────────────────
/** Per-engine crawler access (s2). `unknown` when robots.txt was unreachable —
 *  never guessed. */
export const crawlerAccessSchema = z.object({
    token: z.string(),
    vendor: z.string(),
    purpose: z.enum(['search', 'user_initiated', 'training']),
    access: z.enum(['allowed', 'blocked', 'unknown']),
    /** null for training crawlers even when blocked — blocking those costs no
     *  answer visibility, so it is deliberately not a finding. */
    severity: z.enum(['high', 'medium']).nullable(),
});
/** Raw vs rendered comparison (s3). null when the page could not be rendered —
 *  the agent must then say NOTHING about extractability. */
export const extractabilityEvidenceSchema = z
    .object({
    rawWords: z.number().int().min(0),
    renderedWords: z.number().int().min(0),
    ratio: z.number().min(0).max(1),
    jsOnly: z.object({
        jsonLd: z.boolean(),
        canonical: z.boolean(),
        h1: z.boolean(),
        title: z.boolean(),
    }),
    severity: z.enum(['high', 'medium']).nullable(),
})
    .nullable();
/** What the engines actually sent (s6). A zero here is a FLOOR, not a
 *  measurement — apps strip referrers, AI Overviews traffic reads as organic,
 *  and a cited answer nobody clicks sends nothing at all. */
export const aiReferralEvidenceSchema = z.object({
    engines: z.array(z.object({
        engine: z.string(),
        count: z.number().int().min(0),
        /** Split of `count` by how the visit was recognised. Optional so
         *  snapshots persisted before the tagged channel existed still parse —
         *  absent means "only the referrer channel was measured", never
         *  "no tagged traffic". */
        referred: z.number().int().min(0).optional(),
        tagged: z.number().int().min(0).optional(),
    })),
    total: z.number().int().min(0),
    /** Visits an ENGINE referred — the fact. Unchanged by the tagged channel:
     *  a visit carrying both signals counts here, not there. */
    referred: z.number().int().min(0).optional(),
    /** Visits recognised ONLY by a caller-supplied utm_source. Weaker evidence,
     *  kept apart so nothing can mistake it for the referred count — a tag is
     *  forgeable, a referrer is emitted by the engine (spec 28d71507 D1). */
    tagged: z.number().int().min(0).optional(),
    share: z.number().min(0).max(1),
    isFloorNotMeasurement: z.boolean(),
    windowDays: z.number().int().min(1),
});
/** Off-domain corroboration (spec 28d71507 slice 2, 01701bfe) — the only
 *  signal here that is NOT about the founder's own site. An engine deciding who
 *  to NAME for a buying question is mostly reading other people's pages, so
 *  this reports, per derived buying query, which of those pages name the brand
 *  and which do not.
 *
 *  `checked` is the count of pages we FETCHED AND READ, and it is the reason
 *  this shape has no "missing" count: a page we could not read is absent from
 *  both lists entirely. "We could not read it" and "it does not mention you"
 *  are different facts and only the second is a finding, so the first is never
 *  representable here — the fetcher drops it before this schema sees it. */
export const aiCorroborationEvidenceSchema = z
    .object({
    queries: z.array(z.object({
        /** The buying question, in the market's language. */
        query: z.string().min(1).max(200),
        /** Pages that answer the query AND name the brand. */
        corroborated: z.array(z.object({ url: z.string(), title: z.string() })).max(8),
        /** Pages that answer the query and do NOT name the brand — the
         *  actionable half. Each one is a place the founder could be. */
        gaps: z.array(z.object({ url: z.string(), title: z.string() })).max(8),
        /** Pages fetched and read for this query. Never the number proposed. */
        checked: z.number().int().min(0),
    })),
    /** Pages proposed and then dropped because we could not read them. Carried
     *  so the card can say "4 of 6 read" rather than implying we read all six —
     *  never counted as gaps. */
    unreadable: z.number().int().min(0),
    /** What the sweep cost, so the per-run bound (D4) is observable rather
     *  than asserted. */
    costUsd: z.number().min(0),
})
    .nullable();
export const aiVisibilityEvidenceSchema = z.object({
    url: z.string().url(),
    /** 'ok' | 'missing' | 'unreachable' for robots.txt. */
    robotsStatus: z.enum(['ok', 'missing', 'unreachable']),
    crawlers: z.array(crawlerAccessSchema).default([]),
    extractability: extractabilityEvidenceSchema,
    /** schema.org @type values found on the home page, lowercased. */
    schemaTypes: z.array(z.string()).default([]),
    referrals: aiReferralEvidenceSchema.nullable(),
    /** Off-domain corroboration. Nullable AND optional: null when the sweep ran
     *  and derived no queries, absent on every snapshot persisted before the
     *  sweep existed. Both mean "say nothing about it" — the same silence rule
     *  every other degraded signal here follows. */
    corroboration: aiCorroborationEvidenceSchema.optional(),
    /** The scraped home-page copy the answer_shape read is made against. */
    onPage: z
        .object({ title: z.string(), description: z.string(), textExcerpt: z.string() })
        .nullable(),
    /** What the site SHOULD be saying, from the brand brain. */
    positioning: z.object({
        category: z.string(),
        positioning: z.string(),
        valueProps: z.array(z.string()),
    }),
    /** Detected site builder, when known — decides where a snippet goes. */
    framework: z.string().nullable(),
});
export const aiVisibilityActionStatusSchema = z.enum(['implemented', 'dismissed']);
/** Founder verdict on an ai_visibility card — recorded so Mag won't nag about
 *  a check already acted on, and can phrase continuity on the next weekly run.
 *
 *  Deliberately NOT shared with setSeoActionSchema even though the shapes
 *  match: the two audits reach independent verdicts, and the endpoint that
 *  parses this is what keeps a dismissal on one card from silencing the
 *  other. */
export const setAiVisibilityActionSchema = z.object({
    status: aiVisibilityActionStatusSchema,
    note: z.string().max(600).nullish(),
});
//# sourceMappingURL=mag-ai-visibility.js.map